<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page">
                                    <span>Edit Violation Detail</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>

        <div class="row layout-top-spacing">
            <div class="col-xl-10 col-lg-10 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <router-link to="/violation/parameters/view">
                            <button type="button" class="btn btn-danger mb-2 me-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="feather feather-arrow-left">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12 19 5 12 12 5"></polyline>
                                </svg>
                                Back to Violations Detail List
                            </button>
                        </router-link>
                        <h6>Edit Violation Details...</h6>

                        <!-- Form -->
                        <form @submit.prevent="handleSubmit">
                            <div class="column">
                                <div class="col-md-12">
                                    <div class="mb-4">
                                        <label class="col-form-label" for="violation_id">Select Violation</label>
                                        <select v-model="formData.violation_id" id="violation_id" class="form-control"
                                            required>
                                            <option value="" disabled>Select a violation</option>
                                            <option v-for="violation in violations" :key="violation.id"
                                                :value="violation.id">
                                                {{ violation.title }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="mb-4">
                                        <label class="col-form-label" for="name">Detail Name</label>
                                        <input v-model="formData.name" type="text" id="name" class="form-control"
                                            placeholder="e.g., Max Speed, Location, Reason" required />
                                    </div>

                                    <div class="mb-4">
                                        <label class="col-form-label" for="parameter_type_id">Select Detail
                                            Type</label>
                                        <select v-model="formData.parameter_type_id" id="parameter_type_id"
                                            class="form-control" required>
                                            <option value="" disabled>Select a Detail Type</option>
                                            <option v-for="parameter_type in parameter_types" :key="parameter_type.id"
                                                :value="parameter_type.id">
                                                {{ parameter_type.type }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="mb-4">
                                        <label class="col-form-label">Active Status</label>
                                        <select v-model="formData.is_active" class="form-control">
                                            <option :value="1">Active</option>
                                            <option :value="0">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="text-center mt-3">
                                <div v-if="loading">
                                    <button type="button" class="btn btn-success mb-3 me-3"><span
                                            class="spinner-grow text-white me-2 align-self-center loader-sm">Loading...</span>
                                        Processing
                                    </button>
                                </div>
                                <div v-else>
                                    <button type="submit" class="btn btn-success mb-3 me-3">Submit</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMeta } from "/src/composables/use-meta";
import axios from 'axios';
import { BASE_URL } from '/src/Config/config';
import Swal from 'sweetalert2';
import { useStore } from 'vuex';

useMeta({ title: "Edit Violation Detail" });

const route = useRoute();
const router = useRouter();
const store = useStore();

const formData = ref({
    id: null,
    violation_id: '',
    name: '',
    parameter_type_id: '',
    is_active: 1
});

const violations = ref([]);
const parameter_types = ref([]);
const loading = ref(false);

// SweetAlert functions
const showSuccessAlert = (message, redirect = true) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    }).then(() => {
        if (redirect) {
            router.push('/violation/parameters/view');
        }
    });
};

const showErrorAlert = (message, redirect = false) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    }).then(() => {
        if (redirect) {
            router.push('/violation/parameters/view');
        }
    });
};

// Load data on component mount
onMounted(async () => {
    try {
        loading.value = true;

        // Load data concurrently
        await Promise.all([
            loadViolations(),
            loadParameterTypes(),
            loadViolationParameterIfNeeded()
        ]);

    } catch (error) {
        console.error('Error loading data:', error);
        showErrorAlert('Failed to load required data', true);
    } finally {
        loading.value = false;
    }
});

// Load violations list
const loadViolations = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/violations`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        violations.value = response.data;
    } catch (error) {
        console.error('Error loading violations:', error);
        throw error;
    }
};

// Load parameter types
const loadParameterTypes = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/parameter-types`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        parameter_types.value = response.data;
    } catch (error) {
        console.error('Error loading Detail types:', error);
        throw error;
    }
};

// Load the parameter to edit if ID exists
const loadViolationParameterIfNeeded = async () => {
    const paramId = Number(route.params.id);
    if (paramId) {
        await loadViolationParameter(paramId);
    } else {
        showErrorAlert('No Detail ID provided', true);
    }
};

// Load single violation parameter
const loadViolationParameter = async (id) => {
    try {
        const response = await axios.get(`${BASE_URL}/violation-parameters/${id}`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        formData.value = {
            id: response.data.id,
            violation_id: response.data.violation_id,
            name: response.data.name,
            parameter_type_id: response.data.parameter_type_id,
            is_active: response.data.is_active
        };
    } catch (error) {
        console.error('Error loading violation Detail:', error);
        let errorMessage = 'Failed to load violation Detail';

        if (error.response?.status === 404) {
            errorMessage = 'Violation Detail not found';
        } else if (error.request) {
            errorMessage = 'Network error - please check your connection';
        }

        showErrorAlert(errorMessage, true);
        throw error;
    }
};

// Handle form submission
const handleSubmit = async () => {
    try {
        loading.value = true;

        // Update existing parameter
        await axios.put(`${BASE_URL}/violation-parameters/${formData.value.id}`,
            formData.value,
            { // Config object
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            }
        );
        showSuccessAlert('Violation Detail updated successfully!');

    } catch (error) {
        console.error('Error updating violation Detail:', error);
        let errorMessage = 'Failed to update violation Detail';

        if (error.response) {
            if (error.response.status === 404) {
                errorMessage = 'Violation Detail not found';
            } else if (error.response.data?.errors) {
                errorMessage = Object.values(error.response.data.errors).join('\n');
            } else if (error.response.data?.message) {
                errorMessage = error.response.data.message;
            }
        } else if (error.request) {
            errorMessage = 'Network error - please check your connection';
        }

        showErrorAlert(errorMessage);
    } finally {
        loading.value = false;
    }
};
</script>